// Inventory landing banner (slice 4g). Shows up when there are
// receipts the operator hasn't yet reviewed -- either OCR-skipped or
// OCR'd-but-partially-posted. Clicking "Review now" switches the
// Inventory panel to the Receipts tab so the operator lands directly
// in the place where they can act.
//
// Stays silent (renders null) at count===0. No skeleton during the
// first fetch -- the banner is opportunistic chrome, not core
// inventory data, so showing a flash of nothing then a banner is
// better than reserving space and shifting layout when the count
// resolves to 0 (which is the steady state for healthy orgs).
//
// Mobile-default-collapsed (dev-sync 29299696): on phones the full
// banner ate ~30% of the visible viewport before the user could see
// any inventory data. We now render a compact one-line summary on
// mobile (count + "Review now" + chevron) and let the user expand to
// see the per-receipt preview list. Their choice persists in
// localStorage so re-collapsing or re-expanding sticks across
// navigation. Desktop is unchanged -- always expanded.
//
// The longer-term plan (note 1 in the dev-sync) is to fold persistent
// info banners into the Firefly intent system. The Firefly is still a
// production firefly under `src/components/activity/firefly/`), so
// this slice ships the mobile collapse behavior while we keep the
// banner as the production surface.
import { useEffect, useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import { useSidebarBreakpoint } from "@/components/shell/Sidebar/useSidebarBreakpoint";
import type { MfgReceiptAttentionItem } from "@/lib/manufacturing/types";

const EXPANDED_STORAGE_KEY = "bb:mfg.receiptsAttentionBanner.mobileExpanded";

type Props = {
  count: number;
  items: MfgReceiptAttentionItem[];
  onSwitchToReceipts: () => void;
};

function readPersistedExpanded(): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(EXPANDED_STORAGE_KEY);
    if (raw === "1") return true;
    if (raw === "0") return false;
    return null;
  } catch {
    return null;
  }
}

function writePersistedExpanded(expanded: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(EXPANDED_STORAGE_KEY, expanded ? "1" : "0");
  } catch {
    // Quota / private mode -- silently ignore. Toggling still works
    // for this session, just won't persist.
  }
}

export function ManufacturingReceiptsAttentionBanner({
  count,
  items,
  onSwitchToReceipts,
}: Props) {
  const sidebarVariant = useSidebarBreakpoint();
  const isMobile = sidebarVariant === "mobile";

  // Source of truth for whether the preview list + body copy show.
  // Mobile defaults to collapsed (saves ~30% of the viewport on
  // 360x640) but persists the user's last toggle so re-expanding
  // sticks. Desktop+rail always render expanded -- the space cost
  // is negligible and persistent collapse there would be confusing.
  const [expanded, setExpanded] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    if (window.innerWidth > 767) return true;
    const persisted = readPersistedExpanded();
    return persisted ?? false;
  });

  // Re-sync on viewport variant changes: dragging a desktop window
  // narrow enough to trip the mobile breakpoint should drop into the
  // collapsed default unless the user already opted in.
  useEffect(() => {
    if (sidebarVariant !== "mobile") {
      setExpanded(true);
      return;
    }
    const persisted = readPersistedExpanded();
    setExpanded(persisted ?? false);
  }, [sidebarVariant]);

  if (count === 0) return null;

  const preview = items
    .filter(
      (i) =>
        i.status === "partial_posted" ||
        i.status === "none_posted" ||
        i.status === "not_extracted"
    )
    .slice(0, 3);

  const showFullBody = !isMobile || expanded;

  function toggleExpanded() {
    setExpanded((prev) => {
      const next = !prev;
      writePersistedExpanded(next);
      return next;
    });
  }

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-base font-semibold text-amber-900 dark:text-amber-200">
              {count} receipt{count === 1 ? "" : "s"} need{count === 1 ? "s" : ""} attention
            </span>
            {showFullBody ? (
              <span className="text-[11px] text-amber-700 dark:text-amber-300">
                uploaded but not fully posted into inventory
              </span>
            ) : null}
            <InlineHelp
              label="What 'needs attention' means"
              dismissKey="mfg.receipts.attentionBanner"
            >
              A receipt shows up here when it&rsquo;s been uploaded but either
              hasn&rsquo;t been OCR&rsquo;d yet OR has extracted lines that haven&rsquo;t been
              posted to a cost entry. Click <strong>Review now</strong> to
              jump to the Receipts tab. On a receipt where the rest of the
              lines aren&rsquo;t going into inventory (mixed personal + business
              purchase, etc.), use <strong>Dismiss alert</strong> in the
              Review modal to silence it without forcing phantom posts.
            </InlineHelp>
          </div>
          {showFullBody && preview.length > 0 ? (
            <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-amber-800 dark:text-amber-300">
              {preview.map((p) => (
                <li key={p.receipt_id}>
                  <span className="font-medium">{p.vendor_name || "(no vendor)"}</span>
                  <span className="text-amber-700 dark:text-amber-400">
                    {" "}
                    · {labelForStatus(p)}
                  </span>
                </li>
              ))}
              {count > preview.length ? (
                <li className="text-amber-700 dark:text-amber-400">+{count - preview.length} more…</li>
              ) : null}
            </ul>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={onSwitchToReceipts}
            className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-zinc-900 dark:text-amber-200 dark:hover:bg-zinc-800"
          >
            Review now
          </button>
          {isMobile ? (
            <button
              type="button"
              onClick={toggleExpanded}
              aria-label={expanded ? "Collapse banner" : "Expand banner"}
              aria-expanded={expanded}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-zinc-900 dark:text-amber-200 dark:hover:bg-zinc-800"
            >
              <span aria-hidden="true" className="text-xs">
                {expanded ? "▴" : "▾"}
              </span>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function labelForStatus(item: MfgReceiptAttentionItem): string {
  switch (item.status) {
    case "not_extracted":
      return "not OCR'd yet";
    case "none_posted":
      return `0 of ${item.extracted_line_count} lines posted`;
    case "partial_posted":
      return `${item.posted_line_count} of ${item.extracted_line_count} lines posted`;
    default:
      return "";
  }
}
